// src/components/results/FlowVolumeLoopsSection.jsx
import React, { memo, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";
import {
  buildFlowVolumeDatasets,
  computeGlobalAxisBounds,
  computeLoopPeaks,
  fmtNumber,
} from "../../utils/loopsHelpers";
import UnifiedFlowOverlay from "./UnifiedFlowOverlay";
import { FlowLoopMetricBarChart } from "./FlowLoopMetricBarChart";
import { FlowVolumeMetricsTable } from "./FlowVolumeMetricsTable";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

const zeroAxesPlugin = {
  id: "zeroAxesPlugin",
  afterDraw(chart, args, opts) {
    // Only run when explicitly enabled on this chart
    if (!opts || opts.enabled !== true) return;

    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const x0 = x.getPixelForValue(0);
    const y0 = y.getPixelForValue(0);

    const drawX0 = opts.drawX0 !== false; // default true
    const drawY0 = opts.drawY0 !== false; // default true

    ctx.save();
    ctx.strokeStyle = opts.color || getPrimaryColour();
    ctx.globalAlpha = opts.alpha ?? 0.25;
    ctx.lineWidth = opts.lineWidth ?? 1;

    // vertical x=0
    if (drawX0 && x0 >= chartArea.left && x0 <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(x0, chartArea.top);
      ctx.lineTo(x0, chartArea.bottom);
      ctx.stroke();
    }

    // horizontal y=0
    if (drawY0 && y0 >= chartArea.top && y0 <= chartArea.bottom) {
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y0);
      ctx.lineTo(chartArea.right, y0);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const peakLinesPlugin = {
  id: "peakLinesPlugin",
  afterDatasetsDraw(chart, args, opts) {
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const peaks = opts?.peaks;
    if (!peaks?.hasPeaks) return;

    const y0 = y.getPixelForValue(0);
    const xEx = x.getPixelForValue(peaks.vExPeak);
    const xIn = x.getPixelForValue(peaks.vInPeak);
    const yEx = y.getPixelForValue(peaks.fExPeak);
    const yIn = y.getPixelForValue(peaks.fInPeak);

    ctx.save();
    ctx.strokeStyle = opts?.color || getPrimaryColour();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;

    // Exhale peak line: from 0 to fExPeak at vExPeak
    if (xEx >= chartArea.left && xEx <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(xEx, y0);
      ctx.lineTo(xEx, yEx);
      ctx.stroke();
    }

    // Inhale peak line: from 0 to fInPeak at vInPeak
    if (xIn >= chartArea.left && xIn <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(xIn, y0);
      ctx.lineTo(xIn, yIn);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const quadrantLabelsPlugin = {
  id: "quadrantLabelsPlugin",
  afterDraw(chart, args, opts) {
    // Only run when explicitly enabled on this chart
    if (!opts || opts.enabled !== true) return;

    const { ctx, chartArea } = chart;

    ctx.save();
    ctx.fillStyle = opts?.color || getPrimaryColour();
    ctx.font = "700 18px sans-serif";
    ctx.textBaseline = "top";

    ctx.textAlign = "left";
    ctx.fillText("A", chartArea.left + 8, chartArea.top + 6);

    ctx.textAlign = "right";
    ctx.fillText("B", chartArea.right - 8, chartArea.top + 6);

    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText("C", chartArea.left + 8, chartArea.bottom - 6);

    ctx.textAlign = "right";
    ctx.fillText("D", chartArea.right - 8, chartArea.bottom - 6);

    ctx.restore();
  },
};

const shadingPlugin = {
  id: "shadingPlugin",
  beforeDatasetsDraw(chart, args, opts) {
    const { ctx, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const peaks = opts?.peaks;
    const points = opts?.points || [];
    if (!peaks?.hasPeaks || points.length < 2) return;

    const y0 = y.getPixelForValue(0);

    // --- helpers ------------------------------------------------------------

    // Insert an interpolated boundary point at splitX so left/right meet exactly.
    function splitWithBoundary(sortedPts, splitX) {
      if (!Array.isArray(sortedPts) || sortedPts.length < 2) {
        return { left: [], right: [] };
      }
      if (!Number.isFinite(splitX)) {
        return { left: sortedPts.slice(), right: [] };
      }

      // Fast path: if splitX is outside range, nothing to do.
      const minX = sortedPts[0].x;
      const maxX = sortedPts[sortedPts.length - 1].x;
      if (splitX <= minX)
        return { left: [sortedPts[0]], right: sortedPts.slice() };
      if (splitX >= maxX)
        return {
          left: sortedPts.slice(),
          right: [sortedPts[sortedPts.length - 1]],
        };

      // Find the segment [i, i+1] that straddles splitX
      let i = -1;
      for (let k = 0; k < sortedPts.length - 1; k++) {
        const a = sortedPts[k];
        const b = sortedPts[k + 1];
        if (a.x <= splitX && splitX <= b.x) {
          i = k;
          break;
        }
      }

      // If we somehow didn't find a straddle (e.g. repeated x or non-monotonic), fallback.
      if (i === -1) {
        // fallback split (still better than gaps, but might not be perfect)
        const left = sortedPts.filter((p) => p.x <= splitX);
        const right = sortedPts.filter((p) => p.x >= splitX);
        return { left, right };
      }

      const a = sortedPts[i];
      const b = sortedPts[i + 1];

      // If already have an exact point at splitX, reuse it.
      let boundary;
      if (a.x === splitX) boundary = { x: a.x, y: a.y };
      else if (b.x === splitX) boundary = { x: b.x, y: b.y };
      else {
        // Linear interpolation
        const t = (splitX - a.x) / (b.x - a.x || 1);
        boundary = { x: splitX, y: a.y + t * (b.y - a.y) };
      }

      // Build left/right including boundary in both
      const left = sortedPts.slice(0, i + 1);
      const right = sortedPts.slice(i + 1);

      // Ensure boundary is last of left and first of right (avoid duplicates if same x exists)
      if (left.length === 0 || left[left.length - 1].x !== boundary.x)
        left.push(boundary);
      else left[left.length - 1] = boundary;

      if (right.length === 0 || right[0].x !== boundary.x)
        right.unshift(boundary);
      else right[0] = boundary;

      return { left, right };
    }

    function drawFill(pts, fillStyle) {
      if (!pts || pts.length < 2) return;

      ctx.save();
      ctx.fillStyle = fillStyle;
      ctx.globalAlpha = 0.6;

      ctx.beginPath();
      const p0 = pts[0];
      ctx.moveTo(x.getPixelForValue(p0.x), y.getPixelForValue(p0.y));

      for (let i = 1; i < pts.length; i++) {
        const p = pts[i];
        ctx.lineTo(x.getPixelForValue(p.x), y.getPixelForValue(p.y));
      }

      // Close to baseline
      const last = pts[pts.length - 1];
      ctx.lineTo(x.getPixelForValue(last.x), y0);
      ctx.lineTo(x.getPixelForValue(p0.x), y0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // --- build sorted exhale/inhale ----------------------------------------

    const ex = points
      .filter((p) => p.y > 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
      .slice()
      .sort((a, b) => a.x - b.x);

    const inn = points
      .filter((p) => p.y < 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
      .slice()
      .sort((a, b) => a.x - b.x);

    if (ex.length < 2 || inn.length < 2) return;

    // --- split with boundary & draw ----------------------------------------

    // Exhale split at vExPeak
    const exSplitX = peaks.vExPeak;
    const { left: exLeftPts, right: exRightPts } = splitWithBoundary(
      ex,
      exSplitX,
    );

    drawFill(exLeftPts, opts?.colors?.exLeft || "#f8a0d6");
    drawFill(exRightPts, opts?.colors?.exRight || "#a56498");

    // Inhale split at vInPeak (your prior convention: >= peak vs < peak)
    const inSplitX = peaks.vInPeak;
    const { left: inLeftPts, right: inRightPts } = splitWithBoundary(
      inn,
      inSplitX,
    );

    // IMPORTANT:
    // inn is sorted ascending by x; if you conceptually want "right side = x >= vInPeak",
    // then `right` from splitWithBoundary corresponds to x >= splitX.
    drawFill(inRightPts, opts?.colors?.inRight || "#744267"); // x >= vInPeak
    drawFill(inLeftPts, opts?.colors?.inLeft || "#553150"); // x <= vInPeak
  },
};

ChartJS.register(
  ...registerables,
  zeroAxesPlugin,
  peakLinesPlugin,
  quadrantLabelsPlugin,
  shadingPlugin,
);

function isObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function normalizeFirestoreLoops(recordJson) {
  const flowLoopRoot =
    recordJson?.results?.mpm?.flow_loop ||
    recordJson?.flow_loop ||
    recordJson?.flowLoop ||
    null;

  const breaths =
    (Array.isArray(recordJson?.flowLoopBreaths) &&
      recordJson.flowLoopBreaths) ||
    (Array.isArray(flowLoopRoot?.breaths) && flowLoopRoot.breaths) ||
    [];

  if (!breaths.length) {
    return { loops: [], metrics: {} };
  }

  const loops = breaths
    .map((breath, idx) => {
      const points = Array.isArray(breath?.points)
        ? breath.points.filter(
            (p) =>
              typeof p?.x === "number" &&
              Number.isFinite(p.x) &&
              typeof p?.y === "number" &&
              Number.isFinite(p.y),
          )
        : [];

      if (points.length < 2) return null;

      return {
        loopName: `Breath ${breath?.breathNumber ?? idx + 1}`,
        points,
        sourceBreath: {
          breathNumber: breath?.breathNumber ?? idx + 1,
          metrics: {
            isSpirometryManeuverBreath:
              breath?.isSpirometryManeuverBreath === true,
          },
        },
      };
    })
    .filter(Boolean);

  const metrics =
    (isObject(flowLoopRoot?.summary_metrics) && flowLoopRoot.summary_metrics) ||
    (isObject(flowLoopRoot?.summaryMetrics) && flowLoopRoot.summaryMetrics) ||
    {};

  return { loops, metrics };
}

function readFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readArray(value) {
  return Array.isArray(value) ? value : [];
}

function getCompletedDurationSeconds(recordJson, flowRoot) {
  const explicitDuration =
    readFiniteNumber(recordJson?.duration_seconds) ??
    readFiniteNumber(recordJson?.durationSeconds) ??
    readFiniteNumber(flowRoot?.duration_seconds) ??
    readFiniteNumber(flowRoot?.durationSeconds);

  if (explicitDuration != null) return explicitDuration;

  const processedTimes = readArray(
    recordJson?.["mpm-signal-processed"]?.timesSeconds,
  ).length
    ? readArray(recordJson?.["mpm-signal-processed"]?.timesSeconds)
    : readArray(recordJson?.["mpm-signal-processed"]?.resampledTimesSeconds);

  if (processedTimes.length >= 2) {
    const first = readFiniteNumber(processedTimes[0]);
    const last = readFiniteNumber(processedTimes[processedTimes.length - 1]);
    if (first != null && last != null && last >= first) {
      return last - first;
    }
  }

  const flowLoopBreaths = readArray(flowRoot?.breaths);
  const lastBreathEnd = readFiniteNumber(
    flowLoopBreaths[flowLoopBreaths.length - 1]?.endTimeSeconds,
  );

  return lastBreathEnd ?? 60;
}

function getLoopTimesSeconds(flowRoot) {
  const breaths = readArray(flowRoot?.breaths);
  return breaths
    .map((b) => readFiniteNumber(b?.startTimeSeconds))
    .filter((v) => v != null);
}

function FlowVolumeLoopsSection({
  recordJson,
  title = null,
  hideSpirometryManeuverBreaths = false,
  showOverlay = true,
  showMetricBar = false,
  showMetricsTable = false,
}) {
  const { t } = useTranslation();
  const primary = getPrimaryColour();

  const [expanded, setExpanded] = useState(false);

  const flowRoot = recordJson?.flow_calculator || recordJson;

  const { loops, metrics } = useMemo(() => {
    const firestoreNormalized = normalizeFirestoreLoops(recordJson);

    if (firestoreNormalized.loops.length) {
      const filteredLoops = hideSpirometryManeuverBreaths
        ? firestoreNormalized.loops.filter(
            (loop) =>
              loop?.sourceBreath?.metrics?.isSpirometryManeuverBreath !== true,
          )
        : firestoreNormalized.loops;

      return {
        loops: filteredLoops,
        metrics: firestoreNormalized.metrics,
      };
    }

    const legacyLoops = buildFlowVolumeDatasets(flowRoot);

    return {
      loops: hideSpirometryManeuverBreaths
        ? legacyLoops.filter(
            (loop) =>
              loop?.sourceBreath?.metrics?.isSpirometryManeuverBreath !== true,
          )
        : legacyLoops,
      metrics: flowRoot?.metrics || {},
    };
  }, [recordJson, flowRoot, hideSpirometryManeuverBreaths]);

  const localizedLoops = useMemo(
    () =>
      loops.map((loop, index) => {
        const breathNumber =
          loop?.breathNumber ?? loop?.sourceBreath?.breathNumber ?? index + 1;

        return {
          ...loop,
          loopName: t("results.flowLoops.breathName", {
            defaultValue: "Breath {{count}}",
            count: breathNumber,
          }),
        };
      }),
    [loops, t],
  );

  const [selectedMetric, setSelectedMetric] = useState("");

  // MUST be before any early return (rules-of-hooks)
  const axisBounds = useMemo(
    () => computeGlobalAxisBounds(localizedLoops, 0.05),
    [localizedLoops],
  );

  // Now safe to early return
  if (!localizedLoops.length) return null;

  // Show 4 by default so the 2-col grid looks balanced
  const visibleLoops = expanded ? localizedLoops : localizedLoops.slice(0, 4);

  const sectionTitle =
    title ||
    t("results.flowLoops.sectionTitle", {
      defaultValue: "Flow-Volume Loops",
    });

  return (
    <div className="w-full flex flex-col gap-4">
      <p className="subtitle-text">
        {t("results.flowLoops.subtitle", {
          defaultValue: "Respiratory Flow Volume Loops",
        })}
      </p>

      {/* <OverlayFlowLoops loops={localizedLoops} axisBounds={axisBounds} /> */}
      {showOverlay ? (
        <UnifiedFlowOverlay
          loops={localizedLoops}
          axisBounds={axisBounds}
          durationSeconds={getCompletedDurationSeconds(recordJson, flowRoot)}
          times={
            readArray(recordJson?.times).length
              ? recordJson?.times
              : readArray(recordJson?.["mpm-signal-processed"]?.timesSeconds)
                    .length
                ? recordJson?.["mpm-signal-processed"]?.timesSeconds
                : recordJson?.["mpm-signal-processed"]?.resampledTimesSeconds
          }
          loopTimesS={
            readArray(flowRoot?.flow_loop_times_s).length
              ? flowRoot.flow_loop_times_s
              : getLoopTimesSeconds(flowRoot)
          }
          trailCount={10}
        />
      ) : null}
      <div className="flex items-center justify-between">
        <p className="font-semibold">{sectionTitle}</p>

        {localizedLoops.length > 4 && (
          <button
            className="text-sm underline"
            onClick={() => setExpanded((v) => !v)}
            type="button"
          >
            {expanded
              ? t("results.flowLoops.showFewer", { defaultValue: "Show fewer" })
              : t("results.flowLoops.showAll", {
                  defaultValue: "Show all ({{count}})",
                  count: localizedLoops.length,
                })}
          </button>
        )}
      </div>

      {/* 2-column grid */}
      <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3">
        {visibleLoops.map((loop, idx) => {
          const data = {
            datasets: [
              {
                label: loop.loopName,
                data: loop.points,
                parsing: false,
                pointRadius: 0,
                borderWidth: 2,
                tension: 0.2,
                borderColor: primary,
                backgroundColor: primary,
              },
            ],
          };

          const peaks = computeLoopPeaks(loop.points);

          function zeroOnlyTickLabel(v) {
            const num = Number(v);
            if (!Number.isFinite(num)) return "";
            return Math.abs(num) < 1e-9 ? "0" : "";
          }

          function niceStep(range, targetTicks = 9) {
            if (!Number.isFinite(range) || range <= 0) return 1;
            const raw = range / targetTicks;

            const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
            const norm = raw / pow10;

            let nice;
            if (norm <= 1) nice = 1;
            else if (norm <= 2) nice = 2;
            else if (norm <= 5) nice = 5;
            else nice = 10;

            return nice * pow10;
          }

          const xStep = niceStep(
            (axisBounds?.xMax ?? 0) - (axisBounds?.xMin ?? 0),
            9,
          );
          const yStep = niceStep(
            (axisBounds?.yMax ?? 0) - (axisBounds?.yMin ?? 0),
            9,
          );

          const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },

              // FIX: no nested "tooltip: { tooltip: ... }"
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const x = ctx.raw?.x;
                    const y = ctx.raw?.y;
                    return `V: ${fmtNumber(x, 3)} | F: ${fmtNumber(y, 3)}`;
                  },
                },
              },

              // plugin configs (these will work once registered)
              zeroAxesPlugin: {
                enabled: true,
                color: primary,
                alpha: 0.25,
                lineWidth: 1.5,
                drawX0: true, // vertical x=0
                drawY0: true, // keep y=0 as well (set false if you don’t want it)
              },
              peakLinesPlugin: { color: primary, peaks },
              // Only enabled here
              quadrantLabelsPlugin: { enabled: true, color: primary },
              shadingPlugin: {
                peaks,
                points: loop.points,
                colors: {
                  exLeft: "#f8a0d6",
                  exRight: "#a56498",
                  inLeft: "#553150",
                  inRight: "#744267",
                },
              },
            },
            scales: {
              x: {
                type: "linear",
                title: {
                  display: true,
                  text: t("results.flowLoops.volumeAxis", {
                    defaultValue: "Volume",
                  }),
                },
                min: axisBounds?.xMin,
                max: axisBounds?.xMax,
                ticks: {
                  stepSize: xStep,
                  includeBounds: true,
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  callback: (v) => zeroOnlyTickLabel(v),
                },
              },
              y: {
                title: {
                  display: true,
                  text: t("results.flowLoops.flowAxis", {
                    defaultValue: "Flow",
                  }),
                },
                min: axisBounds?.yMin,
                max: axisBounds?.yMax,
                ticks: {
                  stepSize: yStep,
                  includeBounds: true,
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  callback: (v) => zeroOnlyTickLabel(v),
                },
              },
            },
          };

          return (
            <div key={`${loop.loopName}-${idx}`} className="w-full">
              <p className="text-sm font-medium mb-2">{loop.loopName}</p>
              <div className="w-full h-56 ">
                <Line data={data} options={options} />
              </div>
            </div>
          );
        })}
      </div>

      {showMetricBar ? (
        <FlowLoopMetricBarChart
          metrics={metrics}
          selectedMetric={selectedMetric}
          onSelectMetric={setSelectedMetric}
        />
      ) : null}

      {showMetricsTable ? (
        <FlowVolumeMetricsTable
          metrics={metrics}
          selectedMetric={selectedMetric}
          onSelectMetric={setSelectedMetric}
        />
      ) : null}
    </div>
  );
}

export default memo(FlowVolumeLoopsSection);
